(() => {
  const drawer = document.getElementById('LuxProductDrawer');
  if (!drawer) return;

  const selectors = {
    loading: '[data-lux-drawer-loading]',
    body: '[data-lux-drawer-body]',
    title: '[data-lux-drawer-title]',
    category: '[data-lux-drawer-category]',
    price: '[data-lux-drawer-price]',
    collection: '[data-lux-drawer-collection]',
    gallery: '[data-lux-drawer-gallery]',
    options: '[data-lux-drawer-options]',
    qtyInput: '[data-lux-qty-input]',
    addToCart: '[data-lux-add-to-cart]',
    viewProduct: '[data-lux-view-product]',
    message: '[data-lux-drawer-message]'
  };

  const els = {
    loading: drawer.querySelector(selectors.loading),
    body: drawer.querySelector(selectors.body),
    title: drawer.querySelector(selectors.title),
    category: drawer.querySelector(selectors.category),
    price: drawer.querySelector(selectors.price),
    collection: drawer.querySelector(selectors.collection),
    gallery: drawer.querySelector(selectors.gallery),
    options: drawer.querySelector(selectors.options),
    qtyInput: drawer.querySelector(selectors.qtyInput),
    addToCart: drawer.querySelector(selectors.addToCart),
    viewProduct: drawer.querySelector(selectors.viewProduct),
    message: drawer.querySelector(selectors.message)
  };

  const state = {
    product: null,
    selectedVariant: null,
    selectedOptions: [],
    productUrl: '#',
    activeImage: '',
    productCollection: ''
  };

  const i18n = {
    close: drawer.dataset.i18nClose || 'Close',
    loading: drawer.dataset.i18nLoading || 'Loading...',
    addToCart: drawer.dataset.i18nAddToCart || 'Add to cart',
    soldOut: drawer.dataset.i18nSoldOut || 'Sold out',
    adding: drawer.dataset.i18nAdding || 'Adding...',
    added: drawer.dataset.i18nAdded || 'Added',
    viewProduct: drawer.dataset.i18nViewProduct || 'View product',
    quantity: drawer.dataset.i18nQuantity || 'Quantity',
    productAdded: drawer.dataset.i18nProductAdded || 'Product added to cart.',
    addError: drawer.dataset.i18nAddError || 'Error adding product to cart.',
    loadError: drawer.dataset.i18nLoadError || 'Unable to load this product.',
    diamondColor: drawer.dataset.i18nDiamondColor || 'Diamond color'
  };

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function normalizeText(str) {
    return String(str || '').trim().toLowerCase();
  }

  function isImageOptionName(optionName) {
    const name = normalizeText(optionName);
    return name === 'color' || name === 'colour' || name === 'colore';
  }

  function formatMoney(cents) {
    const format = drawer.dataset.moneyFormat || '${{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }

    return `${(cents / 100).toFixed(2)} €`;
  }

  function setLoading(isLoading) {
    if (els.loading) els.loading.hidden = !isLoading;
    if (els.body) els.body.hidden = isLoading;
    clearMessage();
  }

  function setMessage(text, isError = false) {
    if (!els.message) return;
    els.message.textContent = text || '';
    els.message.classList.toggle('is-error', !!isError);
  }

  function clearMessage() {
    setMessage('');
  }

  function openDrawer() {
    drawer.hidden = false;

    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
    });

    document.documentElement.classList.add('lux-drawer-open');
    document.body.classList.add('lux-drawer-open');
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('lux-drawer-open');
    document.body.classList.remove('lux-drawer-open');

    setTimeout(() => {
      if (!drawer.classList.contains('is-open')) {
        drawer.hidden = true;
      }
    }, 300);
  }

  function getVariantImage(variant) {
    if (!variant) return '';

    if (variant.featured_image && variant.featured_image.src) {
      return variant.featured_image.src;
    }

    if (variant.featured_image && variant.featured_image.url) {
      return variant.featured_image.url;
    }

    return '';
  }

  function getGalleryImages(product, variant) {
    const variantImage = getVariantImage(variant);

    if (variantImage) {
      return [variantImage];
    }

    return product.images && product.images.length ? [product.images[0]] : [];
  }

  function getOptionImage(optionIndex, value) {
    if (!state.product || !state.product.variants) return '';

    const match = state.product.variants.find((variant) => {
      const variantOptions = [variant.option1, variant.option2, variant.option3];
      return variantOptions[optionIndex] === value && getVariantImage(variant);
    });

    return match ? getVariantImage(match) : '';
  }

  function exactVariantMatch(variant, options) {
    const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    return variantOptions.every((value, index) => value === options[index]);
  }

  function partialVariantMatch(variant, options) {
    const variantOptions = [variant.option1, variant.option2, variant.option3];

    return options.every((value, index) => {
      if (!value) return true;
      return variantOptions[index] === value;
    });
  }

  function getDefaultVariant(product, preferredVariantId) {
    let variant = null;

    if (preferredVariantId) {
      variant = product.variants.find((v) => String(v.id) === String(preferredVariantId));
    }

    if (!variant) {
      variant = product.variants.find((v) => v.available) || product.variants[0];
    }

    return variant;
  }

  function setSelectedVariant(variant) {
    if (!variant) return;

    state.selectedVariant = variant;
    state.selectedOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);

    const variantImage = getVariantImage(variant);

    if (variantImage) {
      state.activeImage = variantImage;
    } else {
      const fallbackImages = getGalleryImages(state.product, variant);
      state.activeImage = fallbackImages[0] || '';
    }
  }

  function getOptionStatus(optionIndex, value) {
    const candidateOptions = [...state.selectedOptions];
    candidateOptions[optionIndex] = value;

    const matching = state.product.variants.filter((variant) =>
      partialVariantMatch(variant, candidateOptions)
    );

    if (!matching.length) return 'unavailable';
    if (matching.some((variant) => variant.available)) return 'available';
    return 'soldout';
  }

  function renderPrice() {
    if (!state.selectedVariant || !els.price) return;

    let html = formatMoney(state.selectedVariant.price);

    if (
      state.selectedVariant.compare_at_price &&
      state.selectedVariant.compare_at_price > state.selectedVariant.price
    ) {
      html += ` <span class="lux-drawer__compare-price">${formatMoney(state.selectedVariant.compare_at_price)}</span>`;
    }

    els.price.innerHTML = html;
  }

  function renderCollection() {
    if (!els.collection) return;

    els.collection.textContent = state.productCollection || '';
    els.collection.style.display = state.productCollection ? '' : 'none';
  }

  function renderGallery() {
    if (!els.gallery) return;

    const images = getGalleryImages(state.product, state.selectedVariant);
    const activeImage = state.activeImage || images[0] || '';

    if (!images.length) {
      els.gallery.innerHTML = '';
      return;
    }

    els.gallery.innerHTML = `
      <div class="lux-drawer__gallery-main lux-drawer__gallery-main--single">
        <div class="lux-drawer__gallery-item is-active">
          <img src="${escapeHtml(activeImage)}" alt="${escapeHtml(state.product.title)}" loading="lazy">
        </div>
      </div>
    `;
  }

  function renderOptions() {
    if (!els.options) return;

    const options = state.product.options || [];

    const html = options
      .map((option, optionIndex) => {
        //hide group if no variations or only 1 option
        if (!option.values || option.values.length <= 1) {
          return '';
        }

        const useImageButtons = isImageOptionName(option.name);

        const buttons = option.values
          .map((value) => {
            const isActive = state.selectedOptions[optionIndex] === value;
            const status = getOptionStatus(optionIndex, value);
            const imageUrl = useImageButtons ? getOptionImage(optionIndex, value) : '';

            let buttonContent = escapeHtml(value);
            let extraClass = '';

            if (useImageButtons && imageUrl) {
              extraClass = ' lux-drawer__option-btn--image';
              buttonContent = `
                <img
                  src="${escapeHtml(imageUrl)}"
                  alt="${escapeHtml(value)}"
                  loading="lazy"
                  class="lux-drawer__option-image"
                >
                <span class="visually-hidden">${escapeHtml(value)}</span>
              `;
            }

            return `
              <button
                type="button"
                class="lux-drawer__option-btn${extraClass} ${isActive ? 'is-active' : ''} ${status === 'soldout' ? 'is-soldout' : ''}"
                data-option-index="${optionIndex}"
                data-option-value="${escapeHtml(value)}"
                aria-label="${escapeHtml(value)}"
                ${status === 'unavailable' ? 'disabled' : ''}
              >
                ${buttonContent}
              </button>
            `;
          })
          .join('');

        const groupLabel = useImageButtons
          ? (state.selectedOptions[optionIndex] || option.name)
          : option.name;

        return `
          <div class="lux-drawer__group">
            <div class="lux-drawer__label drawer_label_prod">${escapeHtml(groupLabel)}</div>
            <div class="lux-drawer__options-list ${useImageButtons ? 'lux-drawer__options-list--image' : ''}">
              ${buttons}
            </div>
          </div>
        `;
      })
      .join('');

    els.options.innerHTML = html;
  }

  function renderActions() {
    const variant = state.selectedVariant;
    if (!variant) return;

    if (els.viewProduct) {
      els.viewProduct.href = state.productUrl;
      if (!els.viewProduct.textContent.trim()) {
        els.viewProduct.textContent = i18n.viewProduct;
      }
    }

    if (!els.addToCart) return;

    if (variant.available) {
      els.addToCart.disabled = false;
      els.addToCart.textContent = i18n.addToCart;
    } else {
      els.addToCart.disabled = true;
      els.addToCart.textContent = i18n.soldOut;
    }
  }

  function renderDrawer() {
    if (!state.product || !state.selectedVariant) return;

    if (els.category) {
      els.category.textContent = state.product.type || '';
    }

    if (els.title) {
      els.title.textContent = state.product.title || '';
    }

    renderPrice();
    renderCollection();
    renderGallery();
    renderOptions();
    renderActions();

    if (els.qtyInput) {
      els.qtyInput.value = 1;
      els.qtyInput.setAttribute('aria-label', i18n.quantity);
    }

    setLoading(false);
  }

  function applyOptionSelection(optionIndex, value) {
    const candidateOptions = [...state.selectedOptions];
    candidateOptions[optionIndex] = value;

    const matching = state.product.variants.filter((variant) =>
      partialVariantMatch(variant, candidateOptions)
    );

    if (!matching.length) return;

    const exact = matching.find((variant) => exactVariantMatch(variant, candidateOptions));
    const preferred = exact || matching.find((variant) => variant.available) || matching[0];

    setSelectedVariant(preferred);
    renderDrawer();
  }

  async function loadProduct(handle, productUrl, preferredVariantId) {
    setLoading(true);

    state.product = null;
    state.selectedVariant = null;
    state.selectedOptions = [];
    state.activeImage = '';
    state.productUrl = productUrl || `${window.Shopify.routes.root}products/${handle}`;

    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js`, {
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Failed to load product');

      const product = await response.json();
      state.product = product;

      const defaultVariant = getDefaultVariant(product, preferredVariantId);
      setSelectedVariant(defaultVariant);
      renderDrawer();
    } catch (error) {
      setLoading(false);
      setMessage(i18n.loadError, true);
      console.error(error);
    }
  }

  async function addToCart() {
    if (!state.selectedVariant || !els.addToCart) return;

    clearMessage();

    const quantity = Math.max(1, parseInt((els.qtyInput && els.qtyInput.value) || '1', 10));

    els.addToCart.disabled = true;
    const originalText = state.selectedVariant.available ? i18n.addToCart : i18n.soldOut;
    els.addToCart.textContent = i18n.adding;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: state.selectedVariant.id,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Add to cart failed');
      }

      setMessage(i18n.productAdded);
      els.addToCart.textContent = i18n.added;

      document.documentElement.dispatchEvent(
        new CustomEvent('lux:productAdded', {
          detail: {
            variantId: state.selectedVariant.id,
            quantity: quantity
          }
        })
      );

      setTimeout(() => {
        els.addToCart.disabled = false;
        els.addToCart.textContent = originalText;
      }, 1200);
    } catch (error) {
      els.addToCart.disabled = false;
      els.addToCart.textContent = originalText;
      setMessage(i18n.addError, true);
      console.error(error);
    }
  }

  document.addEventListener('click', (event) => {
    const openTrigger = event.target.closest('[data-lux-drawer-open]');
    if (openTrigger) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      const handle = openTrigger.dataset.productHandle;
      const productUrl = openTrigger.dataset.productUrl || `${window.Shopify.routes.root}products/${handle}`;
      const variantId = openTrigger.dataset.variantId || '';
      const productCollection = openTrigger.dataset.productCollection || '';

      if (!handle) return;

      state.productCollection = productCollection;

      openDrawer();
      loadProduct(handle, productUrl, variantId);
      return;
    }

    const closeTrigger = event.target.closest('[data-lux-drawer-close]');
    if (closeTrigger) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      closeDrawer();
      return;
    }

    const optionButton = event.target.closest('[data-option-index][data-option-value]');
    if (optionButton) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      if (optionButton.disabled) return;

      const optionIndex = parseInt(optionButton.dataset.optionIndex, 10);
      const optionValue = optionButton.dataset.optionValue;

      applyOptionSelection(optionIndex, optionValue);
      return;
    }

    if (event.target.closest('[data-lux-qty-minus]')) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      if (!els.qtyInput) return;

      const current = Math.max(1, parseInt(els.qtyInput.value || '1', 10));
      els.qtyInput.value = Math.max(1, current - 1);
      return;
    }

    if (event.target.closest('[data-lux-qty-plus]')) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      if (!els.qtyInput) return;

      const current = Math.max(1, parseInt(els.qtyInput.value || '1', 10));
      els.qtyInput.value = current + 1;
      return;
    }

    if (event.target.closest('[data-lux-add-to-cart]')) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      addToCart();
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  if (els.qtyInput) {
    els.qtyInput.addEventListener('input', () => {
      let value = parseInt(els.qtyInput.value || '1', 10);

      if (isNaN(value) || value < 1) value = 1;
      els.qtyInput.value = value;
    });
  }
})();